// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: Customise Home > Layout, Density, Opening a conversation and All chats. Big choice cards, each with a drawn miniature
// of what Home looks like with it, ringed in ink with a check when picked.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import styles from '@app/features/konvo_today/components/settings/CustomiseHome.module.css';
import {
	HOME_ALL_CHATS_GROUPING_DESCRIPTION,
	HOME_ALL_CHATS_GROUPING_LABEL,
	HOME_DENSITY_LABEL,
	HOME_LAYOUT_DESCRIPTION,
	HOME_LAYOUT_LABEL,
	HOME_LAYOUT_NOTE,
	HOME_OPEN_CONVERSATIONS_DESCRIPTION,
	HOME_OPEN_CONVERSATIONS_LABEL,
	homeLayoutIcon,
} from '@app/features/konvo_today/components/settings/CustomiseHomeMeta';
import {
	HomeAllChatsPreview,
	HOME_LAYOUT_PREVIEW,
	HomeDensityPreview,
	HomeOpenPreview,
} from '@app/features/konvo_today/components/settings/HomeLayoutPreviews';
import {SwitchRow} from '@app/features/konvo_today/components/settings/HomeSwipeSettings';
import HomePreferences, {
	HOME_ALL_CHATS_GROUPINGS,
	HOME_LAYOUTS,
	HOME_OPEN_CONVERSATIONS,
	type HomeAllChatsGrouping,
	type HomeDensity,
	type HomeLayout,
	type HomeOpenConversations,
} from '@app/features/konvo_today/state/HomePreferences';
import {CheckIcon, TrayIcon} from '@app/features/ui/icons/Icons';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useRef} from 'react';

const LAYOUT_DESCRIPTOR = msg({message: 'Layout', comment: 'Customise Home section title: how Home is laid out.'});
const LAYOUT_ARIA_DESCRIPTOR = msg({message: 'Home layout', comment: 'Accessible label for the layout choice cards.'});
const DENSITY_DESCRIPTOR = msg({message: 'Density', comment: 'Customise Home section title: how roomy the rows are.'});
const DENSITY_ARIA_DESCRIPTOR = msg({message: 'Home density', comment: 'Accessible label for the density cards.'});
const OPEN_DESCRIPTOR = msg({
	message: 'Opening a conversation',
	comment: 'Customise Home section title: what clicking a chat, email or task on Home does (desktop).',
});
const OPEN_ARIA_DESCRIPTOR = msg({
	message: 'How a conversation opens from Home',
	comment: 'Accessible label for the Pop-up / Side by side choice cards.',
});
const RAIL_DESCRIPTOR = msg({
	message: 'Source rail',
	comment: "Customise Home: switch for the logos on Home's right edge that show one source at a time.",
});
const RAIL_HINT_DESCRIPTOR = msg({
	message: "Logos on Home's right edge show one source at a time, every chat from it.",
	comment: 'Customise Home: line under the Source rail switch.',
});
const ALL_CHATS_DESCRIPTOR = msg({
	message: 'All chats',
	comment: 'Customise Home section title: how the All chats list on Home is grouped.',
});
const ALL_CHATS_ARIA_DESCRIPTOR = msg({
	message: 'How All chats is grouped',
	comment: 'Accessible label for the By day / By connection choice cards.',
});
const ALL_CHATS_HINT_DESCRIPTOR = msg({
	message: 'Newest first either way.',
	comment: 'Customise Home > All chats: line under the By day / By connection choice cards.',
});

interface PreviewChoice<T extends string> {
	value: T;
	name: string;
	description?: string;
	icon?: React.ReactNode;
	preview: React.ReactNode;
}

/** One-of-N big cards with a preview well. Arrow keys move the choice like a radio group. */
function PreviewChoiceCards<T extends string>({
	options,
	value,
	onChange,
	'aria-label': ariaLabel,
	small,
	'data-flx': dataFlx,
}: {
	options: ReadonlyArray<PreviewChoice<T>>;
	value: T;
	onChange: (value: T) => void;
	'aria-label': string;
	small?: boolean;
	'data-flx': string;
}) {
	const refs = useRef<Array<HTMLButtonElement | null>>([]);
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
		const step =
			event.key === 'ArrowRight' || event.key === 'ArrowDown'
				? 1
				: event.key === 'ArrowLeft' || event.key === 'ArrowUp'
					? -1
					: 0;
		if (step === 0) return;
		event.preventDefault();
		const next = (index + step + options.length) % options.length;
		onChange(options[next].value);
		refs.current[next]?.focus();
	};
	return (
		<div role="radiogroup" aria-label={ariaLabel} className={styles.layoutGrid} data-flx={dataFlx}>
			{options.map((option, index) => {
				const checked = option.value === value;
				return (
					<button
						key={option.value}
						ref={(node) => {
							refs.current[index] = node;
						}}
						type="button"
						role="radio"
						aria-checked={checked}
						tabIndex={checked ? 0 : -1}
						className={clsx(styles.layoutCard, checked && styles.layoutCardOn)}
						onClick={() => onChange(option.value)}
						onKeyDown={(event) => handleKeyDown(event, index)}
						data-flx={`${dataFlx}.${option.value}`}
					>
						{checked ? (
							<span className={styles.layoutCheck} aria-hidden="true">
								<CheckIcon size={13} weight="bold" />
							</span>
						) : null}
						<span className={clsx(styles.layoutWell, small && styles.layoutWellSmall)} aria-hidden="true">
							{option.preview}
						</span>
						<span className={styles.layoutName}>
							{option.icon ? (
								<span className={styles.layoutIcon} aria-hidden="true">
									{option.icon}
								</span>
							) : null}
							{option.name}
						</span>
						{option.description ? <span className={styles.layoutDescription}>{option.description}</span> : null}
					</button>
				);
			})}
		</div>
	);
}

export const HomeLayoutSettings = observer(() => {
	const {i18n} = useLingui();
	const layout = HomePreferences.effectiveLayout;
	const options = HOME_LAYOUTS.map((value: HomeLayout) => {
		const Preview = HOME_LAYOUT_PREVIEW[value];
		return {
			value,
			name: i18n._(HOME_LAYOUT_LABEL[value]),
			description: i18n._(HOME_LAYOUT_DESCRIPTION[value]),
			icon: homeLayoutIcon(value),
			preview: <Preview />,
		};
	});
	const densities: ReadonlyArray<HomeDensity> = ['comfortable', 'compact'];
	return (
		<>
			<SettingsSection id="konvo-home-layout" title={i18n._(LAYOUT_DESCRIPTOR)} className={styles.bare}>
				<div className={styles.stack}>
					<PreviewChoiceCards<HomeLayout>
						options={options}
						value={layout}
						onChange={HomePreferences.setLayout}
						aria-label={i18n._(LAYOUT_ARIA_DESCRIPTOR)}
						data-flx="konvo-today.customise-home.layout"
					/>
					<p className={styles.note}>{i18n._(HOME_LAYOUT_NOTE[layout])}</p>
				</div>
			</SettingsSection>
			<SettingsSection id="konvo-home-density" title={i18n._(DENSITY_DESCRIPTOR)} className={styles.bare}>
				<PreviewChoiceCards<HomeDensity>
					small
					options={densities.map((value) => ({
						value,
						name: i18n._(HOME_DENSITY_LABEL[value]),
						preview: <HomeDensityPreview density={value} />,
					}))}
					value={HomePreferences.density}
					onChange={HomePreferences.setDensity}
					aria-label={i18n._(DENSITY_ARIA_DESCRIPTOR)}
					data-flx="konvo-today.customise-home.density"
				/>
			</SettingsSection>
			{/* There is no room beside Home on a phone, and the phone has its own rail. */}
			{!MobileLayout.isEnabled() && (
				<SettingsSection id="konvo-home-open" title={i18n._(OPEN_DESCRIPTOR)} className={styles.bare}>
					<div className={styles.stack}>
						<PreviewChoiceCards<HomeOpenConversations>
							options={HOME_OPEN_CONVERSATIONS.map((value) => ({
								value,
								name: i18n._(HOME_OPEN_CONVERSATIONS_LABEL[value]),
								description: i18n._(HOME_OPEN_CONVERSATIONS_DESCRIPTION[value]),
								preview: <HomeOpenPreview value={value} />,
							}))}
							value={HomePreferences.effectiveOpenConversations}
							onChange={HomePreferences.setOpenConversations}
							aria-label={i18n._(OPEN_ARIA_DESCRIPTOR)}
							data-flx="konvo-today.customise-home.open"
						/>
						<div className={styles.card}>
							<SwitchRow
								icon={<TrayIcon size={20} />}
								title={i18n._(RAIL_DESCRIPTOR)}
								description={i18n._(RAIL_HINT_DESCRIPTOR)}
								value={HomePreferences.sourceRail}
								onChange={HomePreferences.setSourceRail}
								data-flx="konvo-today.customise-home.source-rail"
							/>
						</div>
					</div>
				</SettingsSection>
			)}
			<SettingsSection id="konvo-home-all-chats" title={i18n._(ALL_CHATS_DESCRIPTOR)} className={styles.bare}>
				<div className={styles.stack}>
					<PreviewChoiceCards<HomeAllChatsGrouping>
						options={HOME_ALL_CHATS_GROUPINGS.map((value) => ({
							value,
							name: i18n._(HOME_ALL_CHATS_GROUPING_LABEL[value]),
							description: i18n._(HOME_ALL_CHATS_GROUPING_DESCRIPTION[value]),
							preview: <HomeAllChatsPreview value={value} />,
						}))}
						value={HomePreferences.effectiveAllChatsGrouping}
						onChange={HomePreferences.setAllChatsGrouping}
						aria-label={i18n._(ALL_CHATS_ARIA_DESCRIPTOR)}
						data-flx="konvo-today.customise-home.all-chats"
					/>
					<p className={styles.note}>{i18n._(ALL_CHATS_HINT_DESCRIPTOR)}</p>
				</div>
			</SettingsSection>
		</>
	);
});
